import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { getStyles } from '../../../theme/styles';
import { TextField, FormControl } from '@mui/material';
import courseService from '../../../services/CourseService';
import { CourseDeliveryDTO } from '@beable/roster-backend-client-v1';
import Autocomplete from '@mui/material/Autocomplete';
import ContentCourseDetails, { courseLocation } from './ContentCourseDetails';

interface ProvisionSelectProps {
  courseId: string;
  locationCode: string;
  onChange: any;
  disabled?: boolean;
  variant?: 'filled' | 'outlined';
  className?: string;
  inPanel?: boolean;
}

export default function ContentCourseSelector({ courseId, locationCode, onChange, disabled, variant, className, inPanel }: ProvisionSelectProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [courses, setCourses] = useState<CourseDeliveryDTO[]>([]);
  
  const memoOnChange = useCallback(onChange, []);

  useEffect(() => {
    const fetchCourses = async () => {
      if (locationCode) {
        const data = await courseService.getContentCourses(locationCode);

        if (data) {
          setCourses(data);
          if(data.length === 1) {
            memoOnChange(data[0]);
          }
        } else {
          setCourses([]);
        }
      } else {
        setCourses([]);
      }
    };

    fetchCourses();
  }, [locationCode, memoOnChange]);

  const defaultCourse = courses.find((course) => course.courseId === courseId);

  const handleCourseChange = (event: any, selectedCourse: CourseDeliveryDTO | null) => {
    onChange(selectedCourse);
  }
  
  return (
    <FormControl className={className} sx={inPanel ? styles.pFormControlPanel : styles.pFormControl} variant={ variant || 'standard' }>
    <Autocomplete
      disabled={disabled}
      options={courses}
      value={defaultCourse ?? null}
      getOptionLabel={(course: CourseDeliveryDTO) => `${course.courseName}, Grades: ${course.grades.join(',')}, Location: ${courseLocation(course)}`}
      isOptionEqualToValue={(
        course: CourseDeliveryDTO, 
        value: CourseDeliveryDTO
      ) => {
        if (course.courseId === value?.courseId) {
          return true;
        }
        return false;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search for courses"
          variant={ variant || "standard" }
          size='medium' 
        />
      )}
      onChange={handleCourseChange}
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, course: CourseDeliveryDTO) => {
        return (
          <li {...props}>
            <ContentCourseDetails key={course.courseId} course={course}/>
          </li>
        );
      }}
      size='small'
      id='select-course'
      data-cy='course-selector'
    />
    </FormControl>
  );
}